import { SOCIAL_VERTICAL_ALERT_ID, SOCIAL_VERTICAL_ALERT_TITLE } from '~/lib/social-vertical';
import { TenantState } from './TenantState';
import { TRAVEL_FEE_DESCRIPTION } from '~/lib/schema/travel-fee';
import { addressToArea, addressToStreet, DisplayData, TenantOrderLine } from '~/lib/model';
import Big from 'big.js';
import { TenantJob } from '~/tenants/common/TenantJob';
import { ExtractTenantOrderContextPerformable, TenantOrderContext } from '~/tenants/common/TenantOrderContext';
import { TenantConfig, TenantPackageConfig } from '~/tenants/common/registry';
import { TenantEmailAppointmentContext, TenantEmailOrderContext } from '~/tenants/common/TenantEmailContext';
import { ReactNode } from 'react';
import { datetime } from '~/lib/datettime';
import { OrderCustomTag } from '~/lib/schema/order';
import TenantWrapper from '~/tenants/common/TenantWrapper';
import { SlideshowFeatures } from '~common/model/Slideshow';
import { deliveryCreditDescription, deliveryIsRush, deliveryReleaseLabel } from '~/lib/schema/delivery-menu';

export enum TenantOrderEmailTrigger {
  ORDER_COMPLETE = 'ORDER_COMPLETE',
  APPOINTMENT_LOCKED = 'APPOINTMENT_LOCKED',
}

export type TenantOrderEmail<O extends TenantOrderContext = TenantOrderContext> = {
  id: string;
  once?: boolean;
} & (
  | {
      trigger: TenantOrderEmailTrigger.ORDER_COMPLETE;
      template: <C extends TenantEmailOrderContext<O['metadata'], ExtractTenantOrderContextPerformable<O>>>(
        context: C,
      ) => ReactNode;
    }
  | {
      trigger: TenantOrderEmailTrigger.APPOINTMENT_LOCKED;
      template: <C extends TenantEmailAppointmentContext<O['metadata'], ExtractTenantOrderContextPerformable<O>>>(
        context: C,
      ) => ReactNode;
    }
);

export class TenantOrder<O extends TenantOrderContext = TenantOrderContext, J extends TenantJob<O> = TenantJob<O>> {
  private wrapper: TenantWrapper;

  constructor(
    public readonly tenant: TenantConfig,
    public readonly context: O,
  ) {
    this.wrapper = new TenantWrapper(tenant);
  }

  protected buyer<F extends keyof O['buyer']['metadata']>(field: F): O['buyer']['metadata'][F] {
    return (
      (this.context.buyer.metadata as O['buyer']['metadata'])[field] ??
      (this.context.parent as O['buyer']['metadata'])?.[field]
    );
  }

  get emails(): TenantOrderEmail<O>[] {
    return [];
  }

  get microsite() {
    return false;
  }

  get slideshows(): SlideshowFeatures | null {
    return null;
  }

  get confirmed() {
    return true;
  }

  get taxable() {
    return true;
  }

  get domain(): string | null {
    return null;
  }

  get jobs(): J[] {
    // TODO: how to make typesafe?
    // @ts-ignore
    return this.context.jobs.map((job) =>
      this.wrapper.job(
        this.wrapper.performable(job.performable_id).config,
        this.context,
        job.metadata,
        job.id,
        job.provider ? this.wrapper.provider(job.provider.id) : undefined,
      ),
    );
  }

  get tags(): OrderCustomTag[] {
    return [];
  }

  sendNotification(): { attachments: string[] } {
    return { attachments: [] };
  }

  get location() {
    return this.context.location;
  }

  get alerts(): { id: string; title: string }[] {
    const alerts: { id: string; title: string }[] = [];

    // Every company's: raised for as long as the refund waits for a person
    // (lib/social-vertical.ts has why it is raised here and nowhere else).
    if (this.context.socialVerticalPerson) {
      alerts.push({ id: SOCIAL_VERTICAL_ALERT_ID, title: SOCIAL_VERTICAL_ALERT_TITLE });
    }

    return alerts;
  }

  info(): Array<DisplayData> {
    const info: DisplayData[] = [];

    if (this.context.address) {
      info.push({
        name: 'Address',
        value: `${addressToStreet(this.context.address)}, ${addressToArea(this.context.address)}`,
      });
    }

    info.push({
      name: 'Preferred Providers',
      value:
        this.context.buyer.preferred_providers.length > 0
          ? this.context.buyer.preferred_providers.map((p) => this.tenant.providers[p]).map((p) => `${p.first}`)
          : 'First Available',
      highlight: true,
      schedule: true,
    });

    // THE DELIVERY PROMISE, on an order that carries one (2 Oct 2026): which
    // option was sold and when the photos are due, for the customer (their
    // order page and the order confirmation), the office, the board and the
    // photographer (the row is in their calendar invite too). An order
    // placed without the promise has no such row, so no invite already sent
    // gains a line and goes out again, and no email changes.
    const release = this.context.delivery_release;

    if (release) {
      info.push({
        name: 'Delivery',
        value: deliveryReleaseLabel(release, this.context.timezone),
        customer: true,
        provider: true,
        schedule: true,
        highlight: deliveryIsRush(release),
      });
    }

    return info;
  }

  revenueLines(): TenantOrderLine[] {
    let lines: TenantOrderLine[] = [];

    for (const job of this.jobs) {
      lines = [...lines, ...job.revenueLines().map((line) => ({ ...line, job_id: job.id }))];
    }

    // DELIVERY TIME (9/17/26): the company's own menu option, priced as it
    // was sold. Tenants with a hand-written speed line (Twist's 'speed')
    // never set context.delivery, so the two cannot double up.
    const delivery = this.context.delivery;

    if (delivery && new Big(delivery.fee || '0').gt(0)) {
      lines.push({
        id: 'delivery',
        description: `Delivery: ${delivery.name}`,
        amount: new Big(delivery.fee),
        // Taxed only when the company taxed delivery speed when this was sold (DeliveryMenu.speed_taxable).
        ...(delivery.taxable ? { taxable: true } : {}),
      });
    }

    // THE SPEED AN OFFERING CARRIES (Express), on a taxed line of its own,
    // for an order placed while the company taxed delivery speed: the intake
    // took exactly this much out of the offering's untaxed package line
    // (catalog-order-plan.ts catalogPlanDelivery), so the total is the price
    // the customer was shown, and the speed is taxed like a picked one.
    if (delivery?.included_taxable && new Big(delivery.included || '0').gt(0)) {
      lines.push({
        id: 'delivery_included',
        description: `Delivery: ${delivery.name} (included)`,
        amount: new Big(delivery.included!),
        taxable: true,
      });
    }

    // TRAVEL FEE OPT-IN (lib/schema/travel-fee.ts): only while the photographer
    // the agent requested (and accepted the fee for) is the CURRENT photographer
    // on a live visit (`assigned`, worked out by OrderEntity: a pending
    // reassignment, a removed or canceled visit do not count).
    const travel = this.context.travel_optin;

    if (travel?.assigned && new Big(travel.fee || '0').gt(0)) {
      lines.push({ id: 'travel', description: TRAVEL_FEE_DESCRIPTION, amount: new Big(travel.fee) });
    }

    // LATE DELIVERY CREDIT (2 Oct 2026): a paid-for speed that was released
    // late is given back, as a line of its own rather than by quietly
    // dropping the fee, so the invoice says what happened. The amount is on
    // the order's promise, written with the release (delivery-release.ts), so
    // there is one credit per order however many times this is computed. It
    // is taxed as the speed was: untaxed unless the company taxed delivery
    // speed when it was sold, and then the credit takes the same tax back off
    // (`paid_taxable`, stamped by delivery-promise.ts speedPaidTaxed).
    const credit = this.context.delivery_release?.credit;

    if (credit && new Big(credit).gt(0)) {
      lines.push({
        id: 'delivery_late_credit',
        description: deliveryCreditDescription(this.context.delivery_release!.option_name),
        amount: new Big(credit).neg(),
        ...(this.context.delivery_release!.paid_taxable ? { taxable: true } : {}),
      });
    }

    return lines;
  }

  expenseLines(): TenantOrderLine[] {
    let lines: TenantOrderLine[] = [];

    for (const job of this.jobs) {
      lines = [...lines, ...job.expenseLines().map((line) => ({ ...line, job_id: job.id }))];
    }

    return lines;
  }

  fees(): Big {
    return this.revenueLines()
      .filter((line) => line.fee)
      .reduce((sum, line) => sum.plus(line.amount), Big(0));
  }

  revenue(): Big {
    return this.revenueLines().reduce((sum, line) => sum.plus(line.amount), Big(0));
  }

  expense(): Big {
    return this.expenseLines().reduce((sum, line) => sum.plus(line.amount), Big(0));
  }

  get onsite(): number {
    return this.jobs.reduce((sum, job) => sum + job.onsite(), 0);
  }

  states(): TenantState[] {
    return [];
  }

  get hasAppointments() {
    return this.context.appointments.length > 0;
  }

  get hasActiveAppointments() {
    return this.context.appointments.filter((a) => !a.canceled).length > 0;
  }

  get requestedOnWeekend() {
    return this.context.requested.some((f) => datetime(f.day, this.location.timezone).weekday >= 6);
  }

  needsDelivery(): boolean {
    return false;
  }

  protected package(): { config: TenantPackageConfig<O>; amount: Big } | null {
    const packageConfig = this.tenant.packages
      .map(({ config }) => config)
      .filter((config) => {
        if(config.eligible && !config.eligible(this.context)) {
          return false;
        }

        return config.performables
          // .map((p) => ('id' in p ? p.id : p.config.id))
          .every((p) =>
            this.jobs.some((j) => {
              const id = 'id' in p ? p.id : p.config.id;

              if (j.performable.id !== id) {
                return false;
              }

              if ('config' in p) {
                for (const key in p.metadata) {
                  if (j.metadata[key] !== p.metadata[key]) {
                    return false;
                  }
                }
              }

              return true;
            }),
          );
        },
      )
      .reduce<TenantPackageConfig | null>((acc, config) => {
        if (acc !== null) {
          return config.performables.length > acc.performables.length ? config : acc;
        }

        return config;
      }, null);

    if (!packageConfig) {
      return null;
    }

    let amount = Big(0);

    if (packageConfig.percentage) {
      const pkgJobs = this.jobs.filter((j) =>
        packageConfig.performables.map((p) => ('id' in p ? p.id : p.config.id)).includes(j.performable.id),
      );
      const discountable = pkgJobs.reduce((acc, j) => {
        return acc.plus(j.revenue(true));
      }, new Big(0));

      amount = discountable.times(packageConfig.percentage).neg();
    } else if (packageConfig.amount) {
      amount = Big(packageConfig.amount).neg();
    }

    return {
      config: packageConfig,
      amount,
    };
  }
}
